import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit3, X, Upload, Search } from 'lucide-react';

export default function AppWelcomeSliders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', file: null, previewUrl: '' });

  // 1. Fetch Records on Mount
  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://metapi.emedhatechnologies.com/api/applanding/student-management'); // Update port/URL if needed
      if (!res.ok) throw new Error('Failed to fetch records');
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setFormData({
        title: item.title,
        description: item.description,
        file: null,
        previewUrl: item.image || ''
      });
    } else {
      setEditingId(null);
      setFormData({ title: '', description: '', file: null, previewUrl: '' });
    }
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, file, previewUrl });
    }
  };

  // 2. Handle Create / Update Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    if (formData.file) {
      data.append('image', formData.file);
    }

    try {
      let url = 'https://metapi.emedhatechnologies.com/api/applanding/student-management';
      let method = 'POST';

      if (editingId) {
        url = `https://metapi.emedhatechnologies.com/api/applanding/student-management/${editingId}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method: method,
        body: data
      });

      if (!res.ok) throw new Error('Failed to save record');

      await fetchRecords(); // Refresh list from backend
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving record:', err);
      alert('Failed to save record.');
    }
  };

  // 3. Handle Delete Operation
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const res = await fetch(`https://metapi.emedhatechnologies.com/api/applanding/student-management/${id}`, {
          method: 'DELETE'
        });

        if (!res.ok) throw new Error('Failed to delete record');

        await fetchRecords(); // Refresh list from backend
      } catch (err) {
        console.error('Error deleting record:', err);
        alert('Failed to delete record.');
      }
    }
  };

  return (
    <div style={styles.container}>
      {/* Header & Controls */}
      <div style={styles.topBar}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.searchInput}
          />
        </div>
        <button onClick={() => handleOpenModal()} style={styles.primaryBtn}>
          <Plus size={16} style={{ marginRight: '6px' }} /> Add New Record
        </button>
      </div>

      {/* Data Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.dataTable}>
          <thead>
            <tr>
              <th style={styles.tableTh}>Image</th>
              <th style={styles.tableTh}>Title</th>
              <th style={styles.tableTh}>Description</th>
              <th style={styles.tableTh}>Date Added</th>
              <th style={styles.tableTh}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={styles.emptyCell}>Loading records...</td></tr>
            ) : currentItems.length === 0 ? (
              <tr><td colSpan="5" style={styles.emptyCell}>No records found.</td></tr>
            ) : (
              currentItems.map((item) => (
                <tr key={item.id} style={styles.tableTr}>
                  <td style={styles.tableTd}>
                    <img
                      src={item.image ? `${item.image}` : 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&auto=format&fit=crop&q=80'}
                      alt={item.title}
                      style={styles.thumbnail}
                    />
                  </td>
                  <td style={styles.tableTd}><strong>{item.title}</strong></td>
                  <td style={styles.tableTd}>{item.description}</td>
                  <td style={styles.tableTd}>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
                  <td style={styles.tableTd}>
                    <div style={styles.actionGroup}>
                      <button onClick={() => handleOpenModal(item)} style={styles.iconButton} title="Edit">
                        <Edit3 size={16} color="#2563eb" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} style={styles.iconButton} title="Delete">
                        <Trash2 size={16} color="#dc2626" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      <div style={styles.paginationBar}>
        <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
        <div style={styles.pageBtnGroup}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            style={{ ...styles.pagBtn, ...(currentPage === 1 && styles.pagBtnDisabled) }}
          >
            Previous
          </button>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            style={{ ...styles.pagBtn, ...(currentPage === totalPages && styles.pagBtnDisabled) }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal for Create / Edit */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{editingId ? 'Edit Record' : 'Add New Record'}</h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.closeBtn}><X size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Title</label>
                <input
                  type="text"
                  required
                  placeholder="Enter title..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Description</label>
                <textarea
                  rows="3"
                  placeholder="Enter description..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.textarea}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Image File</label>
                <div style={styles.fileUploadBox}>
                  <input type="file" accept="image/*" onChange={handleFileChange} style={styles.fileInputHidden} />
                  <div style={styles.fileUploadContent}>
                    {formData.previewUrl ? (
                      <img src={formData.previewUrl} alt="Preview" style={styles.imagePreview} />
                    ) : (
                      <div style={styles.uploadPlaceholder}>
                        <Upload size={20} color="#2563eb" />
                        <span>Click to upload image file</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={styles.modalActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.cancelBtn}>Cancel</button>
                <button type="submit" style={styles.saveBtn}>Save Record</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '16px', fontFamily: 'Inter, sans-serif', background: '#f8fafc', minHeight: '100vh' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: '1', minWidth: '240px' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  searchInput: { width: '100%', padding: '9px 12px 9px 36px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', background: '#fff' },
  primaryBtn: { display: 'flex', alignItems: 'center', padding: '9px 16px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  tableWrapper: { background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' },
  tableTh: { background: '#f1f5f9', color: '#334155', padding: '12px 16px', fontWeight: '600', borderBottom: '1px solid #e2e8f0' },
  tableTd: { padding: '12px 16px', borderBottom: '1px solid #e2e8f0', color: '#1e293b', verticalAlign: 'middle' },
  tableTr: { background: '#fff' },
  thumbnail: { width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' },
  emptyCell: { textAlign: 'center', padding: '30px', color: '#94a3b8' },
  actionGroup: { display: 'flex', gap: '8px' },
  iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' },
  paginationBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '12px 16px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageBtnGroup: { display: 'flex', gap: '8px' },
  pagBtn: { padding: '6px 14px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: '13px', cursor: 'pointer' },
  pagBtnDisabled: { opacity: '0.5', cursor: 'not-allowed' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '16px' },
  modalCard: { background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '480px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '600', color: '#0f172a' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  input: { padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' },
  textarea: { padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', resize: 'vertical' },
  fileUploadBox: { position: 'relative', border: '2px dashed #cbd5e1', borderRadius: '6px', padding: '12px', textAlign: 'center', background: '#f8fafc', cursor: 'pointer' },
  fileInputHidden: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' },
  fileUploadContent: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  uploadPlaceholder: { display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px' },
  imagePreview: { width: '60px', height: '60px', borderRadius: '4px', objectFit: 'cover' },
  modalActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  cancelBtn: { padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', fontSize: '14px', cursor: 'pointer' }
};